import { isEmpty } from '@intelligence/v3/utils/common';
import { numberComparator, newMathFormat } from '@intelligence/utils/commonUtils';
import { SENSORTOWER_METRIC_KEY_MAP } from '@intelligence/v3/_config/metrics/keyMap';
import {
  // 单选、多选国家筛选器
  COMPOSED_COUNTRY_SELECT as FILTER_COMPOSED_COUNTRY_SELECT,
  // 时间筛选器
  RANGE_TIME as FILTER_RANGE_TIME,
  // 品类筛选器
  GENRETOR as FILTER_GENRE_SELECT,
  // Mobile Overlap筛选器
  MOBILE_OVERLAP as FILTER_MOBILE_OVERLAP,
} from '@intelligence/components/Filter/FilterBase/_config';
import { SORTABLE_METRICS } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/constants/metrics';
import { toDateTypeNor } from '@/pages/intelligence/utils/common';
import type { IDataSourceMetric } from '@intelligence/types/common';
import type {
  IMobileDetailsOverlapRank,
  IMobileDetailsOverlapRanksFetchParams,
  IMobileDetailsOverlapTopRanksMetrics,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/Overlap/types';

export const getInitialFetchParams = (
  isOperate: boolean,
  filterExtend: any,
  defaultFetchParams: IMobileDetailsOverlapRanksFetchParams,
): IMobileDetailsOverlapRanksFetchParams => {
  if (!isOperate) return defaultFetchParams;
  /** 国家筛选器部分参数 **/
  const composedCountrySelectFilterExtend = filterExtend?.[FILTER_COMPOSED_COUNTRY_SELECT]?.value;
  const composedCountrySelectFilterExtendCountries = composedCountrySelectFilterExtend?.countries;
  const composedCountrySelectDefaultCountries = defaultFetchParams?.markets;
  const composedCountrySelectInitialCountries =
    composedCountrySelectFilterExtendCountries?.length === 1 &&
    !['global', 'global_wo_china'].includes(composedCountrySelectFilterExtendCountries[0])
      ? composedCountrySelectFilterExtendCountries
      : composedCountrySelectDefaultCountries;
  /** 国家筛选器部分参数 **/

  /** 时间筛选器部分参数 **/
  const rangeTimeFilterExtend = filterExtend?.[FILTER_RANGE_TIME];

  const rangeTimeFilterExtendGranularity = rangeTimeFilterExtend?.granularity;
  const rangeTimeFilterExtendStartDate = rangeTimeFilterExtend?.start_date;
  const rangeTimeFilterExtendEndDate = rangeTimeFilterExtend?.end_date;
  const rangeTimeDefaultGranularity = defaultFetchParams?.granularity;
  const rangeTimeDefaultStartDate = defaultFetchParams?.start_date;
  const rangeTimeDefaultEndDate = defaultFetchParams?.end_date;
  // 此处overlap只继承月粒度
  const isMonthType = toDateTypeNor[rangeTimeFilterExtendGranularity] === 'month';
  const rangeTimeInitialGranularity =
    rangeTimeFilterExtendGranularity && isMonthType ? rangeTimeFilterExtendGranularity : rangeTimeDefaultGranularity;
  const rangeTimeInitialStartDate =
    rangeTimeFilterExtendStartDate && isMonthType ? rangeTimeFilterExtendStartDate : rangeTimeDefaultStartDate;
  const rangeTimeInitialEndDate =
    rangeTimeFilterExtendEndDate && isMonthType ? rangeTimeFilterExtendEndDate : rangeTimeDefaultEndDate;
  /** 时间筛选器部分参数 **/

  /** 品类筛选器部分参数 **/
  const genreFilterExtend = filterExtend?.[FILTER_GENRE_SELECT];
  const genreFilterExtendGenreSource = genreFilterExtend?.genre_source;
  const genreFilterExtendIeggGenre = genreFilterExtend?.iegg_genre;
  const genreFilterExtendSensorTowerGenre = genreFilterExtend?.sensortower_genre;
  const genreFilterExtendAppannieGenre = genreFilterExtend?.appannie_genre;
  const genreDefaultGenreSource = defaultFetchParams?.genre_source;
  const genreDefaultIeggGenre = defaultFetchParams?.iegg_genre;
  const genreDefaultSensorTowerGenre = defaultFetchParams?.sensortower_genre;
  const genreDefaultAppannieGenre = defaultFetchParams?.appannie_genre;
  const genreInitialGenreSource = genreFilterExtendGenreSource || genreDefaultGenreSource;
  const genreInitialIeggGenre = genreFilterExtendIeggGenre?.length ? genreFilterExtendIeggGenre : genreDefaultIeggGenre;
  const genreInitialSensorTowerGenre = genreFilterExtendSensorTowerGenre?.length
    ? genreFilterExtendSensorTowerGenre
    : genreDefaultSensorTowerGenre;
  const genreInitialAppannieGenre = genreFilterExtendAppannieGenre?.length
    ? genreFilterExtendAppannieGenre
    : genreDefaultAppannieGenre;
  /** 品类筛选器部分参数 **/

  /** mobile overlap筛选器部分参数 **/
  const mobileOverlapFilterExtend = filterExtend?.[FILTER_MOBILE_OVERLAP]?.value;
  const mobileOverlapDefault = defaultFetchParams?.mobile_overlap;
  const mobileOverlapInitial = mobileOverlapFilterExtend || mobileOverlapDefault;
  /** mobile overlap筛选器部分参数 **/

  return {
    ...defaultFetchParams,
    markets: composedCountrySelectInitialCountries,
    granularity: rangeTimeInitialGranularity,
    start_date: rangeTimeInitialStartDate,
    end_date: rangeTimeInitialEndDate,
    genre_source: genreInitialGenreSource,
    iegg_genre: genreInitialIeggGenre,
    sensortower_genre: genreInitialSensorTowerGenre,
    appannie_genre: genreInitialAppannieGenre,
    mobile_overlap: mobileOverlapInitial,
  };
};

export const calcTopRanksMetrics = (ranks: IMobileDetailsOverlapRank[]): IMobileDetailsOverlapTopRanksMetrics => {
  if (!Array.isArray(ranks)) return {};
  const result: IMobileDetailsOverlapTopRanksMetrics = {};
  for (const metric of SORTABLE_METRICS) {
    if (!metric) continue;
    // 这里排序一定要新建数组排序，避免影响原数组的排序
    const descOrderRanks = [...ranks].sort((a, b) => {
      const aMetric = a?.metric_list?.find((item) => item?.metric === metric);
      const bMetric = b?.metric_list?.find((item) => item?.metric === metric);
      const aMetricValue = Number.parseFloat(aMetric?.value as any);
      const bMetricValue = Number.parseFloat(bMetric?.value as any);
      return numberComparator(aMetricValue, bMetricValue, false);
    });
    result[metric] = descOrderRanks.slice(0, 10).map((item) => item?.id);
  }
  return result;
};

export const formatPercentNum = (num: any): string => {
  let numTmp: any = Number.parseFloat(num);
  if (Number.isNaN(numTmp)) return '-';
  numTmp = numTmp.toFixed(2);
  return `${numTmp}%`;
};

export const handleMetrics = (metrics: IDataSourceMetric[]): IDataSourceMetric[] => {
  if (!Array.isArray(metrics)) return [];
  return metrics.map((metric) => {
    if (metric?.metric !== SENSORTOWER_METRIC_KEY_MAP.CROSS_APP_USAGE) return metric;
    const metricValueNum = Number.parseFloat(metric?.value as any);
    return {
      ...metric,
      value: metricValueNum * 100,
    };
  });
};

const METRICS_FORMATTERS: IRecord<Function> = {
  [SENSORTOWER_METRIC_KEY_MAP.CROSS_APP_USAGE]: formatPercentNum,
  [SENSORTOWER_METRIC_KEY_MAP.CROSS_AFFINITY]: (value: any) => {
    const valueTmp = newMathFormat(value, true);
    return valueTmp && valueTmp !== '-' ? `${valueTmp}x` : valueTmp;
  },
};

const getMetricFormatter = (metric?: string): Function | null => {
  if (!metric) return null;
  return METRICS_FORMATTERS[metric] || null;
};

export const formatMetricValue = (metricKey?: string, metricValue?: any): any => {
  const formatter = getMetricFormatter(metricKey);
  const formatterValue = formatter?.(metricValue) || metricValue;
  return isEmpty(formatterValue) ? '-' : formatterValue;
};

export const formatYAxisLabel = (metric: string, value: any): string => {
  if (metric === SENSORTOWER_METRIC_KEY_MAP.CROSS_APP_USAGE) return `${value}%`;
  if (metric === SENSORTOWER_METRIC_KEY_MAP.CROSS_AFFINITY) return `${value}(x)`;
  return value;
};
